import React, { useState } from 'react';
import axios from 'axios';
import { getAccessToken } from '../authUtils';

interface TestResult {
  ok: boolean;
  data: unknown;
  duration: number;
  status?: number;
}

interface TestDefinition {
  id: string;
  label: string;
  method: string;
  path: () => string;
  fn: () => Promise<unknown>;
  inputs?: React.ReactNode;
}

interface Props {
  baseApiUrl: string;
}

export default function ApiTestPanel({ baseApiUrl }: Props) {
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [itemId, setItemId]       = useState('');
  const [folderId, setFolderId]   = useState('root');
  const [searchQuery, setSearchQuery] = useState('test');
  const [tagInput, setTagInput]   = useState('urgent, review');
  const [versionId, setVersionId] = useState('');

  async function run(testId: string, fn: () => Promise<unknown>) {
    setLoading((prev) => ({ ...prev, [testId]: true }));
    const start = Date.now();
    try {
      const data = await fn();
      setResults((prev) => ({
        ...prev,
        [testId]: { ok: true, data, duration: Date.now() - start },
      }));
    } catch (err: unknown) {
      const e = err as { response?: { data?: unknown; status?: number }; message: string };
      const errData = e.response?.data ?? { error: e.message };
      setResults((prev) => ({
        ...prev,
        [testId]: { ok: false, data: errData, duration: Date.now() - start, status: e.response?.status },
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [testId]: false }));
    }
  }

  const tests: TestDefinition[] = [
    {
      id: 'list-folder',
      label: 'List folder contents',
      method: 'GET',
      path: () => `/api/documents/folder/${folderId || 'root'}`,
      fn: () => axios.get(`${baseApiUrl}/api/documents/folder/${folderId || 'root'}`).then((r) => r.data),
      inputs: (
        <input
          className="input"
          placeholder="Folder ID (default: root)"
          value={folderId}
          onChange={(e) => setFolderId(e.target.value)}
        />
      ),
    },
    {
      id: 'search',
      label: 'Full-text search',
      method: 'GET',
      path: () => `/api/documents/search?q=${searchQuery}`,
      fn: () => axios.get(`${baseApiUrl}/api/documents/search`, { params: { q: searchQuery } }).then((r) => r.data),
      inputs: (
        <input
          className="input"
          placeholder="Search query"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      ),
    },
    {
      id: 'download',
      label: 'Download file (opens URL)',
      method: 'GET',
      path: () => `/api/documents/download/${itemId}`,
      fn: () => {
        if (!itemId) throw new Error('Item ID is required');
        window.open(`${baseApiUrl}/api/documents/download/${itemId}`, '_blank');
        return Promise.resolve({ message: 'Download triggered in new tab' });
      },
      inputs: (
        <input
          className="input"
          placeholder="Item ID (required)"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
        />
      ),
    },
    {
      id: 'open-in-sharepoint',
      label: 'Get item metadata / Open in SharePoint',
      method: 'GET',
      path: () => `/api/documents/${itemId}`,
      fn: async () => {
        if (!itemId) throw new Error('Item ID is required');
        const data = await axios.get<{ webUrl?: string }>(`${baseApiUrl}/api/documents/${itemId}`).then((r) => r.data);
        if (data.webUrl) window.open(data.webUrl, '_blank');
        return data;
      },
      inputs: (
        <input
          className="input"
          placeholder="Item ID (required)"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
        />
      ),
    },
    {
      id: 'versions',
      label: 'Get version history',
      method: 'GET',
      path: () => `/api/documents/${itemId}/versions`,
      fn: () => {
        if (!itemId) throw new Error('Item ID is required');
        return axios.get(`${baseApiUrl}/api/documents/${itemId}/versions`).then((r) => r.data);
      },
      inputs: (
        <input
          className="input"
          placeholder="Item ID (required)"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
        />
      ),
    },
    {
      id: 'get-tags',
      label: 'Get tags',
      method: 'GET',
      path: () => `/api/documents/${itemId}/tags`,
      fn: () => {
        if (!itemId) throw new Error('Item ID is required');
        return axios.get(`${baseApiUrl}/api/documents/${itemId}/tags`).then((r) => r.data);
      },
      inputs: (
        <input
          className="input"
          placeholder="Item ID (required)"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
        />
      ),
    },
    {
      id: 'set-tags',
      label: 'Set tags',
      method: 'PATCH',
      path: () => `/api/documents/${itemId}/tags`,
      fn: () => {
        if (!itemId) throw new Error('Item ID is required');
        const tags = tagInput.split(',').map((t) => t.trim()).filter(Boolean);
        return axios.patch(`${baseApiUrl}/api/documents/${itemId}/tags`, { tags }).then((r) => r.data);
      },
      inputs: (
        <div className="flex gap-2">
          <input
            className="input"
            placeholder="Item ID (required)"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
          />
          <input
            className="input"
            placeholder="Tags (comma-separated)"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
          />
        </div>
      ),
    },
    {
      id: 'delta-sync',
      label: 'Run delta sync (SharePoint → SQL)',
      method: 'POST',
      path: () => '/api/documents/sync/delta',
      fn: () => axios.post(`${baseApiUrl}/api/documents/sync/delta`).then((r) => r.data),
    },
    {
      id: 'full-scan',
      label: 'Full scan — reindex all documents into vector DB',
      method: 'POST',
      path: () => '/api/documents/sync/full-scan',
      fn: () => axios.post(`${baseApiUrl}/api/documents/sync/full-scan`).then((r) => r.data),
    },
    {
      id: 'subscribe',
      label: 'Create webhook subscription',
      method: 'POST',
      path: () => '/api/documents/sync/subscribe',
      fn: () => axios.post(`${baseApiUrl}/api/documents/sync/subscribe`).then((r) => r.data),
    },
    {
      id: 'version-download',
      label: 'Download specific version',
      method: 'GET',
      path: () => `/api/documents/${itemId}/versions/${versionId}/content`,
      fn: () => {
        if (!itemId || !versionId) throw new Error('Both Item ID and Version ID are required');
        window.open(`${baseApiUrl}/api/documents/${itemId}/versions/${versionId}/content`, '_blank');
        return Promise.resolve({ message: 'Version download triggered in new tab' });
      },
      inputs: (
        <div className="flex gap-2">
          <input
            className="input"
            placeholder="Item ID"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
          />
          <input
            className="input"
            placeholder="Version ID"
            value={versionId}
            onChange={(e) => setVersionId(e.target.value)}
          />
        </div>
      ),
    },
  ];

  const methodColour: Record<string, string> = {
    GET:   'bg-green-100 text-green-800',
    POST:  'bg-blue-100 text-blue-800',
    PATCH: 'bg-yellow-100 text-yellow-800',
  };

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
        <strong>API Test Panel</strong> — calls are made directly to <code className="font-mono">{baseApiUrl}</code>.
        Shared ID fields (Item ID, Folder ID) update across all tests.
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">SHARED FIELDS — paste IDs here to use across all tests</p>
        <div className="flex gap-3 flex-wrap">
          <div className="flex-1 min-w-48">
            <label className="text-xs text-gray-500">Item ID</label>
            <input className="input mt-1" placeholder="Paste a file item ID" value={itemId} onChange={(e) => setItemId(e.target.value)} />
          </div>
          <div className="flex-1 min-w-48">
            <label className="text-xs text-gray-500">Folder ID</label>
            <input className="input mt-1" placeholder="root" value={folderId} onChange={(e) => setFolderId(e.target.value)} />
          </div>
        </div>
      </div>

      {tests.map((test) => {
        const result = results[test.id];
        const isLoading = loading[test.id];
        return (
          <div key={test.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${methodColour[test.method] ?? 'bg-gray-100 text-gray-700'}`}>
                {test.method}
              </span>
              <code className="text-xs text-gray-600 flex-1">{test.path()}</code>
              <span className="text-sm font-medium text-gray-800">{test.label}</span>
              <button
                onClick={() => run(test.id, test.fn)}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-w-16"
              >
                {isLoading ? '...' : 'Run'}
              </button>
            </div>

            {test.inputs && (
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                {test.inputs}
              </div>
            )}

            {result && (
              <div className={`px-4 py-3 ${result.ok ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-medium ${result.ok ? 'text-green-700' : 'text-red-700'}`}>
                    {result.ok ? '✓ OK' : `✗ Error${result.status ? ` ${result.status}` : ''}`}
                  </span>
                  <span className="text-xs text-gray-400">{result.duration}ms</span>
                </div>
                <pre className="text-xs text-gray-800 overflow-auto max-h-48 bg-white border border-gray-200 rounded p-2">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        );
      })}

      <UploadTest baseApiUrl={baseApiUrl} folderId={folderId} />
    </div>
  );
}

function UploadTest({ baseApiUrl, folderId }: { baseApiUrl: string; folderId: string }) {
  const [file, setFile]         = useState<File | null>(null);
  const [result, setResult]     = useState<TestResult | null>(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  function runUpload() {
    if (!file) return;
    setUploading(true);
    setResult(null);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    const xhr = new XMLHttpRequest();
    const start = Date.now();

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      setUploading(false);
      const duration = Date.now() - start;
      try {
        const data = JSON.parse(xhr.responseText) as unknown;
        setResult({ ok: xhr.status === 201, data, duration, status: xhr.status });
      } catch {
        setResult({ ok: false, data: { error: xhr.responseText }, duration, status: xhr.status });
      }
    };
    xhr.onerror = () => {
      setUploading(false);
      setResult({ ok: false, data: { error: 'Network error' }, duration: Date.now() - start });
    };

    xhr.open('POST', `${baseApiUrl}/api/documents/upload/${folderId || 'root'}`);
    getAccessToken().then((token) => {
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
        <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">POST</span>
        <code className="text-xs text-gray-600 flex-1">/api/documents/upload/{folderId || 'root'}</code>
        <span className="text-sm font-medium text-gray-800">Upload file</span>
        <button
          onClick={runUpload}
          disabled={!file || uploading}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-w-16"
        >
          {uploading ? `${progress}%` : 'Run'}
        </button>
      </div>
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
        <input
          type="file"
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); }}
          className="text-sm text-gray-600"
        />
      </div>
      {uploading && (
        <div className="px-4 py-2">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
      {result && (
        <div className={`px-4 py-3 ${result.ok ? 'bg-green-50' : 'bg-red-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs font-medium ${result.ok ? 'text-green-700' : 'text-red-700'}`}>
              {result.ok ? '✓ OK' : `✗ Error ${result.status ?? ''}`}
            </span>
            <span className="text-xs text-gray-400">{result.duration}ms</span>
          </div>
          <pre className="text-xs text-gray-800 overflow-auto max-h-48 bg-white border border-gray-200 rounded p-2">
            {JSON.stringify(result.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
